import { useEffect, useState, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Search, Plus, Pencil, Trash2, Loader2, Database } from "lucide-react";
import { toast } from "sonner";
import { formatTanggal } from "../lib/format";
import { PageHeader } from "./ui/page-header";
import { EmptyState } from "./ui/empty-state";

const PAGE_SIZE = 10;

export const CrudPage = ({
    title, subtitle, endpoint, entityLabel = "Data",
    columns, renderForm, emptyForm, validate, testidBase = "crud",
    mobileRender, // optional (row) => JSX for mobile card content
}) => {
    const { canWrite, canDelete } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);

    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async (query = "") => {
        setLoading(true);
        try {
            const { data } = await api.get(endpoint, { params: query ? { q: query } : {} });
            setItems(data);
        } catch (err) { toast.error(errorMessage(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);
    useEffect(() => {
        const t = setTimeout(() => { load(q); setPage(1); }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q]);

    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const pageItems = useMemo(
        () => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [items, page]
    );

    const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
    const openEdit = (row) => { setEditing(row); setForm({ ...emptyForm, ...row }); setDialogOpen(true); };

    const handleSave = async () => {
        const err = validate ? validate(form) : null;
        if (err) { toast.error(err); return; }
        setSubmitting(true);
        try {
            if (editing) {
                await api.put(`${endpoint}/${editing.id}`, form);
                toast.success(`${entityLabel} berhasil diperbarui`);
            } else {
                await api.post(endpoint, form);
                toast.success(`${entityLabel} berhasil ditambahkan`);
            }
            setDialogOpen(false);
            await load(q);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSubmitting(false); }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            await api.delete(`${endpoint}/${confirmDelete.id}`);
            toast.success(`${entityLabel} berhasil dihapus`);
            setConfirmDelete(null);
            await load(q);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setDeleting(false); }
    };

    const ActionButtons = ({ row }) => (
        <div className="flex items-center gap-1 justify-end">
            {canWrite() && (
                <button onClick={() => openEdit(row)} data-testid={`${testidBase}-edit-btn-${row.id}`}
                    className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]"
                    style={{ color: "var(--text-secondary)" }} title="Edit">
                    <Pencil className="w-4 h-4" />
                </button>
            )}
            {canDelete() && (
                <button onClick={() => setConfirmDelete(row)} data-testid={`${testidBase}-delete-btn-${row.id}`}
                    className="p-2 rounded-lg hover:bg-[var(--apple-red-soft)]"
                    style={{ color: "var(--apple-red)" }} title="Hapus">
                    <Trash2 className="w-4 h-4" />
                </button>
            )}
        </div>
    );

    const primaryKey = columns[0]?.key;

    return (
        <div className="space-y-4 md:space-y-5">
            <PageHeader
                title={title}
                description={subtitle}
                actions={canWrite() && (
                    <Button onClick={openCreate} data-testid={`${testidBase}-add-button`}
                        className="rounded-lg gap-2 text-white font-medium"
                        style={{ background: "var(--apple-blue)" }}>
                        <Plus className="w-4 h-4" /> Tambah {entityLabel}
                    </Button>
                )}
            />

            {/* Search */}
            <div className="rounded-xl bg-white p-3" style={{ border: "1px solid var(--border)" }}>
                <div className="relative max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                    <Input
                        placeholder={`Cari ${entityLabel.toLowerCase()}...`}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid={`${testidBase}-search-input`}
                        className="pl-9 rounded-lg h-10"
                        style={{ background: "#fff", border: "1px solid var(--border)" }}
                    />
                </div>
            </div>

            {/* MOBILE: card list */}
            <div className="md:hidden space-y-2">
                {loading ? (
                    <div className="py-12 flex justify-center">
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--apple-blue)" }} />
                    </div>
                ) : pageItems.length === 0 ? (
                    <div className="rounded-xl bg-white" style={{ border: "1px solid var(--border)" }}>
                        <EmptyState
                            icon={Database}
                            title={`Belum ada ${entityLabel.toLowerCase()}`}
                            description={`Klik "Tambah ${entityLabel}" untuk memulai.`}
                            testid={`${testidBase}-empty-mobile`}
                        />
                    </div>
                ) : pageItems.map((row) => (
                    <div key={row.id} className="rounded-xl bg-white p-4"
                        style={{ border: "1px solid var(--border)" }}
                        data-testid={`${testidBase}-card-${row.id}`}>
                        {mobileRender ? mobileRender(row) : (
                            <>
                                <div className="font-medium text-sm mb-1" style={{ color: "var(--text-primary)" }}>
                                    {row[primaryKey] ?? "-"}
                                </div>
                                {columns.slice(1).filter(c => c.key !== "created_at").map((c) => {
                                    const val = c.render ? c.render(row) : (row[c.key] ?? "-");
                                    return (
                                        <div key={c.key} className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                                            <span className="font-medium">{c.header}:</span> <span style={{ color: "var(--text-secondary)" }}>{val}</span>
                                        </div>
                                    );
                                })}
                                {row.created_at && (
                                    <div className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>
                                        Dibuat {formatTanggal(row.created_at)}
                                    </div>
                                )}
                            </>
                        )}
                        <div className="flex items-center justify-end mt-2 pt-2" style={{ borderTop: "1px solid var(--border-soft)" }}>
                            <ActionButtons row={row} />
                        </div>
                    </div>
                ))}
            </div>

            {/* DESKTOP: table */}
            <div className="hidden md:block rounded-xl overflow-hidden bg-white" style={{ border: "1px solid var(--border)" }}>
                <Table data-testid={`${testidBase}-data-table`}>
                    <TableHeader>
                        <TableRow>
                            {columns.map((c) => (
                                <TableHead key={c.key}
                                    className={`text-xs font-semibold uppercase tracking-wider ${c.className || ""}`}
                                    style={{ color: "var(--text-muted)" }}>
                                    {c.header}
                                </TableHead>
                            ))}
                            <TableHead className="text-right text-xs font-semibold uppercase tracking-wider w-32" style={{ color: "var(--text-muted)" }}>
                                Aksi
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + 1} className="py-12 text-center">
                                    <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: "var(--apple-blue)" }} />
                                </TableCell>
                            </TableRow>
                        ) : pageItems.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + 1}>
                                    <EmptyState
                                        icon={Database}
                                        title={`Belum ada ${entityLabel.toLowerCase()}`}
                                        description={`Klik "Tambah ${entityLabel}" untuk memulai.`}
                                        testid={`${testidBase}-empty-desktop`}
                                    />
                                </TableCell>
                            </TableRow>
                        ) : pageItems.map((row) => (
                            <TableRow key={row.id} data-testid={`${testidBase}-row-${row.id}`}>
                                {columns.map((c) => (
                                    <TableCell key={c.key} className={`text-sm ${c.className || ""}`}
                                        style={{ color: "var(--text-primary)" }}>
                                        {c.render ? c.render(row) :
                                            c.key === "created_at" ? formatTanggal(row[c.key]) : (row[c.key] ?? "-")}
                                    </TableCell>
                                ))}
                                <TableCell className="text-right"><ActionButtons row={row} /></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {!loading && items.length > PAGE_SIZE && (
                    <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: "1px solid var(--border-soft)", background: "var(--bg-subtle)" }}>
                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                            {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, items.length)} dari {items.length}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                data-testid={`${testidBase}-page-prev`} className="rounded-lg h-8">
                                Sebelumnya
                            </Button>
                            <span className="text-xs px-2" style={{ color: "var(--text-secondary)" }}>
                                {page} / {totalPages}
                            </span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages}
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                data-testid={`${testidBase}-page-next`} className="rounded-lg h-8">
                                Berikutnya
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Create/Edit dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid={`${testidBase}-modal-dialog`} className="rounded-xl max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editing ? `Edit ${entityLabel}` : `Tambah ${entityLabel}`}</DialogTitle>
                        <DialogDescription>
                            {editing ? "Perbarui data lalu tekan Simpan." : `Lengkapi data ${entityLabel.toLowerCase()} lalu tekan Simpan.`}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">{renderForm(form, setForm, editing)}</div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}
                            data-testid={`${testidBase}-modal-cancel-button`} className="rounded-lg">
                            Batal
                        </Button>
                        <Button onClick={handleSave} disabled={submitting}
                            data-testid={`${testidBase}-modal-save-button`}
                            className="rounded-lg text-white"
                            style={{ background: "var(--apple-blue)" }}>
                            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Simpan"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="rounded-xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Konfirmasi Hapus</AlertDialogTitle>
                        <AlertDialogDescription>
                            Apakah Anda yakin ingin menghapus <span className="font-semibold" style={{ color: "var(--text-primary)" }}>
                                {confirmDelete?.nama || confirmDelete?.nama_kain || confirmDelete?.nama_item || confirmDelete?.nama_produk || confirmDelete?.username}
                            </span>? Tindakan ini tidak dapat dibatalkan.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`${testidBase}-delete-cancel`} className="rounded-lg">Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} disabled={deleting}
                            data-testid={`confirm-delete-button`}
                            className="rounded-lg text-white"
                            style={{ background: "var(--apple-red)" }}>
                            {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menghapus...</> : "Hapus"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
